import React, { Fragment, useState, useRef, useEffect } from 'react';
import { observer, useObserver } from 'mobx-react';
import { appConfig } from 'cd-eft/config/AppConfig';
// Components

import { CollapseMenu } from '../../common/CollapseMenu/CollapseMenu';

// Common
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { LoadingSpinner } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { Alert } from 'cd-eft/components/common/Alert';
import stores from 'cd-eft/models/Stores';
import { DemIntegration, DemIntegrationRef } from 'cd-eft/components/common/DemIntegration/DemIntegration';
import { ClippedContainerVM } from './ClippedContainerVM';

const setStore = stores.AppStore.surveyEngineeringStore;
const designStore = stores.AppStore.designStore;
interface IClipGridComponentProps {
  designREF?: any;
  vm?: ClippedContainerVM;
  history?: any;
}
const ClipGridComponent = (props: IClipGridComponentProps) => {
  designStore.designElementTypeId = appConfig.hardCodes.designElementTypeSuface //To enable ToolBar on the map for Surface
  const [state, setStateInfo] = useState({
    headerTitle: "",
    onClickInformation: false
  });
  const onClickInformationToggle = (title: string) => {
    setStateInfo(prevState => ({
      headerTitle: title, onClickInformation: !prevState.onClickInformation
    }));
  }
  const [uiState, setUiState] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successGeneratingFile, setSuccessGeneratingFile] = useState(false);
  const demIntegrationRef = useRef<DemIntegrationRef>(null);
  const [demVM, setDemVM] = useState<any>(null);

  useEffect(() => {
    const vm = demIntegrationRef.current?.getVM?.();
    if (vm && vm !== demVM) {
      setDemVM(vm);
    }
  }, [demIntegrationRef.current, demVM]);

  return (
    <>
      {setStore.isLoading && !designStore.isLoading && <Loader />}
      <CollapseMenu
        title={`Define AOI and DEM`}
        showAsHeader={true}
        cardBodyId="prep-sal"
        noIcon={true}
        forceOpen={true}
        isOpen={true}
        className="designelement-container"
        titleClass="collapse-title-span"
        sectionHelp={true}
        footer={<Footer demIntegrationRef={demIntegrationRef} demVM={demVM} uiState={uiState} setUiState={setUiState} isProcessing={isProcessing} setIsProcessing={setIsProcessing} successGeneratingFile={successGeneratingFile} setSuccessGeneratingFile={setSuccessGeneratingFile} />}
        sectionHelpCallback={() => onClickInformationToggle("Define AOI and DEM")}
      >
        <div style={{ position: 'relative', minHeight: '400px', width: '100%' }}>
          {!uiState && <DemIntegration ref={demIntegrationRef} mapView={props.designREF?.view} project_class_id={4} demProjectVM={props.designREF} />}

          {successGeneratingFile && (
            <Alert show={successGeneratingFile} type="success" title={<SuccessMessage />} />
          )}

          {isProcessing && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              minHeight: '400px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              zIndex: 1000,
              boxSizing: 'border-box',
              padding: '40px 20px'
            }}>
              <LoadingSpinner message="Processing DEM and NAIP services..." loading={true} />
            </div>
          )}
        </div>

        {/* NAIP Loading Indicator */}
        {demVM?.suppressNaipServiceLoading && (
          <Loader message="Checking NAIP availability..." />
        )}
      </CollapseMenu>
    </>
  );
};

export const ClipGrid = observer(ClipGridComponent);

const Footer = observer((props) => {
  const [demDownloadUrl, setDemDownloadUrl] = useState<string>('');
  const [naipDownloadUrl, setNaipDownloadUrl] = useState<string>('');
  const [loadingMsg, setLoadingMsg] = useState('');

  const handleSubmit = async () => {
    const demIntegrationVM = props.demIntegrationRef?.current?.getVM?.();
    if (!demIntegrationVM) {
      return;
    }

    props.setIsProcessing(true);
    props.setSuccessGeneratingFile(false);

    try {
      let processingResults = {
        demClipped: false,
        naipProcessed: false
      };

      // Process DEM services if selected and available
      if (demIntegrationVM.in_dem_service_selection === 'service' &&
        demIntegrationVM.isDemServiceAvailable &&
        typeof demIntegrationVM.demClip === 'function') {

        setLoadingMsg('Clipping DEM...');
        const clipResult = await demIntegrationVM.demClip();

        if (clipResult && demIntegrationVM.in_dem_clip_url) {
          setDemDownloadUrl(demIntegrationVM.in_dem_clip_url);
          processingResults.demClipped = true;
        }
      }

      // Process NAIP imagery if selected and available
      if (demIntegrationVM.in_naip_selection === 'service' &&
        demIntegrationVM.isNaipServiceAvailable) {

        setLoadingMsg('Processing NAIP imagery...');

        try {
          const naipResult = await demIntegrationVM.clipNaipImagery();

          if (naipResult && naipResult.href) {
            setNaipDownloadUrl(naipResult.href);
            processingResults.naipProcessed = true;
          }
        } catch (error) {
          // Error handled silently
        }
      }

      // Show success message if any files were generated
      if (processingResults.demClipped || processingResults.naipProcessed) {
        props.setSuccessGeneratingFile(true);
      }

      props.setUiState(false);
    } catch (error) {
      // Error handled silently
    } finally {
      props.setIsProcessing(false);
      setLoadingMsg('');
    }
  };

  const createDownloadLink = async (): Promise<void> => {
    const hasDem = !!demDownloadUrl;
    const hasNaip = !!naipDownloadUrl;

    if (!hasDem && !hasNaip) return;

    try {
      // If both files are available, we'll download them separately in sequence
      if (hasDem && hasNaip) {
        // Download DEM file first
        const demResponse = await fetch(demDownloadUrl);
        if (!demResponse.ok) throw new Error(`Failed to fetch DEM file: ${demResponse.status}`);
        const demBlob = await demResponse.blob();

        const demBlobUrl = window.URL.createObjectURL(demBlob);
        const demLink = document.createElement('a');
        demLink.href = demBlobUrl;
        demLink.download = 'DEM_Surface.tif';
        demLink.style.display = 'none';
        document.body.appendChild(demLink);
        demLink.click();
        document.body.removeChild(demLink);

        // Clean up DEM blob URL
        setTimeout(() => {
          window.URL.revokeObjectURL(demBlobUrl);
        }, 100);

        // Small delay before second download
        await new Promise(resolve => setTimeout(resolve, 500));

        // Download NAIP file second
        const naipResponse = await fetch(naipDownloadUrl);
        if (!naipResponse.ok) throw new Error(`Failed to fetch NAIP file: ${naipResponse.status}`);
        const naipBlob = await naipResponse.blob();

        const naipBlobUrl = window.URL.createObjectURL(naipBlob);
        const naipLink = document.createElement('a');
        naipLink.href = naipBlobUrl;
        naipLink.download = 'NAIP_Imagery.tif';
        naipLink.style.display = 'none';
        document.body.appendChild(naipLink);
        naipLink.click();
        document.body.removeChild(naipLink);

        // Clean up NAIP blob URL
        setTimeout(() => {
          window.URL.revokeObjectURL(naipBlobUrl);
        }, 100);
      }

      // If only one file is available, download that single file
      else if (hasDem) {
        const response = await fetch(demDownloadUrl);
        if (!response.ok) throw new Error(`Failed to fetch DEM file: ${response.status}`);

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'DEM_Surface.tif';
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      }

      else if (hasNaip) {
        const response = await fetch(naipDownloadUrl);
        if (!response.ok) throw new Error(`Failed to fetch NAIP file: ${response.status}`);

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'NAIP_Imagery.tif';
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      }

      // Reset state after successful download to allow new processing
      setDemDownloadUrl('');
      setNaipDownloadUrl('');
      props.setSuccessGeneratingFile(false);
    } catch (error) {
      alert(`Download failed: ${(error as Error).message}`);
    }
  };

  return useObserver(() => {
    const demIntegrationVM = props.demVM || props.demIntegrationRef?.current?.getVM?.();

    const isDemServiceReady = demIntegrationVM?.in_dem_service_selection === 'service' &&
      demIntegrationVM?.isDemServiceAvailable &&
      demIntegrationVM?.in_dem_id; // Add check for actual service selection

    const isNaipServiceReady = demIntegrationVM?.in_naip_selection === 'service' &&
      demIntegrationVM?.isNaipServiceAvailable;

    const isSubmitDisabled = !(isDemServiceReady || isNaipServiceReady);
    const hasAnyDownload = demDownloadUrl || naipDownloadUrl;

    // Determine button text and action based on state
    const isDownloadMode = hasAnyDownload;
    const buttonText = isDownloadMode ? 'Download' : 'Submit';
    const buttonAction = isDownloadMode ? createDownloadLink : handleSubmit;
    const isButtonDisabled = isDownloadMode ? false : isSubmitDisabled;

    return (
      <div>
        <Fragment>
          <div className='short-button-group clip-dem'>
            <button
              className="usa-button ml-3"
              disabled={isButtonDisabled}
              onClick={buttonAction}
            >
              {buttonText}
            </button>
          </div>
        </Fragment>

        {props.isProcessing && <Loader message={loadingMsg} />}
      </div>
    );
  });
});

const SuccessMessage = observer(() => (
  <>
    <div>New file created!</div>
    <div className="mt-2">You can download the files.</div>
  </>
));

export const ProfileDetails = (props: { className?: string }) => {
  return (
    <div className={props.className ? props.className : ''} tabIndex={0}>
      <p>
        NETS utilizes only one surface at a time.
        The use of a DEM surface or a Survey are mutually exclusive.
        The first surface must be deleted to use a different surface.
        If alignments or other features have been created on the original surface,
        they will be maintained to the extent possible.
      </p>
    </div>
  );
};